import { useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { UserIcon } from 'foundations-assets/icons/components/UserIcon';
import { AvatarDisplay } from 'avatar-components/AvatarDisplay';
import FormattedMessage from 'I18n/components/FormattedMessage';
import formatName from 'I18n/utils/formatName';
import unescapedText from 'I18n/utils/unescapedText';
import { useIsUngated } from 'portals-platform-lib';
import { Popover } from './atoms/Popover';
import { GATES } from '../constants/gates';
import { useTicketDetailsParams } from '../hooks/useTicketDetailsParams';
import { useTicketContacts } from '../data/useTicketContacts';
import { useGrantThreadAccess } from '../data/useGrantThreadAccess';
import { getConversationTypeName } from '../utils/getConversationTypeName';
import { formatDateRelativeToCurrentYear } from '../utils/formatDateRelativeToCurrentYear';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ContactRow = ({
  contact,
  disabled,
  onSelect
}) => {
  const displayName = formatName({
    name: contact.name,
    email: contact.email
  });
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    children: /*#__PURE__*/_jsx(Button, {
      use: "transparent",
      disabled: disabled,
      onClick: () => onSelect(contact),
      "data-test-id": "add-people-contact-row",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "400",
        inlineSize: "100%",
        children: [/*#__PURE__*/_jsx(AvatarDisplay, {
          type: "contact",
          displayName: displayName,
          size: "sm"
        }), /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          minInlineSize: "0",
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "heading-100",
            color: "text-core-default",
            textAlign: "start",
            children: displayName
          }), contact.name && contact.email && /*#__PURE__*/_jsx(Text, {
            typography: "detail-100",
            color: "text-core-subtle",
            textAlign: "start",
            children: contact.email
          })]
        })]
      })
    })
  });
};
const AddPeopleContactList = ({
  ticketId,
  isGranting,
  onSelect
}) => {
  const {
    data,
    loading,
    error
  } = useTicketContacts(ticketId);
  if (loading) {
    return /*#__PURE__*/_jsx(Flex, {
      justifyContent: "center",
      padding: "500",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        size: "extra-small",
        "aria-label": unescapedText('customer-portal-app.loading')
      })
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(Text, {
      typography: "body-100",
      paddingInline: "500",
      paddingBlock: "300",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.addPeople.loadError"
      })
    });
  }
  const contacts = (data === null || data === void 0 ? void 0 : data.contacts) || [];
  if (contacts.length === 0) {
    return /*#__PURE__*/_jsx(Text, {
      typography: "body-100",
      paddingInline: "500",
      paddingBlock: "300",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.addPeople.noContacts"
      })
    });
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: contacts.map(contact => /*#__PURE__*/_jsx(ContactRow, {
      contact: contact,
      disabled: isGranting,
      onSelect: onSelect
    }, contact.contactId))
  });
};
const AddPeopleContent = ({
  thread,
  onDone
}) => {
  const {
    ticketId,
    threadId
  } = useTicketDetailsParams();
  const {
    grantThreadAccess,
    isGranting
  } = useGrantThreadAccess(ticketId, threadId);
  const handleSelect = contact => {
    void grantThreadAccess(contact).then(granted => {
      if (granted) {
        onDone();
      }
    });
  };
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    inlineSize: "320px",
    maxInlineSize: "100%",
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      padding: "500",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "heading-100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.addPeople.title"
        })
      }), thread && /*#__PURE__*/_jsx(Text, {
        typography: "detail-100",
        color: "text-core-subtle",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.addPeople.conversationSummary",
          options: {
            conversationType: getConversationTypeName(thread.originalChannel),
            startedAt: formatDateRelativeToCurrentYear(thread.startedAt)
          }
        })
      }), /*#__PURE__*/_jsx(Text, {
        typography: "detail-100",
        color: "text-core-subtle",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.addPeople.description"
        })
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      borderBlockStartWidth: "100",
      borderBlockStartStyle: "solid",
      borderColor: "border-core-subtle",
      paddingBlockStart: "400",
      paddingBlockEnd: "200",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "detail-100",
        color: "text-core-subtle",
        paddingInline: "500",
        paddingBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.addPeople.contactsHeading"
        })
      }), /*#__PURE__*/_jsx(AddPeopleContactList, {
        ticketId: ticketId,
        isGranting: isGranting,
        onSelect: handleSelect
      })]
    })]
  });
};
export const AddPeoplePopover = ({
  thread
}) => {
  const [open, setOpen] = useState(false);
  const isUngated = useIsUngated(GATES.ADD_TO_THREAD);
  if (!isUngated) {
    return null;
  }
  return /*#__PURE__*/_jsx(Popover, {
    arrowSize: "none",
    open: open,
    placement: "bottom right",
    onOpenChange: e => setOpen(e.currentTarget.value),
    content: /*#__PURE__*/_jsx(AddPeopleContent, {
      thread: thread,
      onDone: () => setOpen(false)
    }),
    children: /*#__PURE__*/_jsxs(Button, {
      use: "primary",
      size: "small",
      "aria-expanded": open,
      "data-test-id": "add-people-button",
      onClick: () => setOpen(prev => !prev),
      children: [/*#__PURE__*/_jsx(UserIcon, {
        size: "200"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.addPeople.button"
      })]
    })
  });
};